declare const c3;

/**
 * Provides c3 resructuring of DOM elements to generate charts for input visualisations
 */
export class C3Service {
  private chartTypes = {
    bar: 'bar',
    line: 'line',
    pie: 'pie'
  };
  private activeCharts;

  constructor() {
    this.activeCharts = {};
  }

  /**
   * Generates a chart with the visualisation and data provided
   * @param visualisation the visualisation to create
   * @param layout the data response for the visualisation from qlik
   * @param model the model with actions from qlik
   * @returns the generated c3 object
   */
  public createChart(visualisation: InterACT.Interfaces.IVisualisation, layout, model) {
    if (!layout.qHyperCube ||
        !layout.qHyperCube.qDataPages ||
        !layout.qHyperCube.qDataPages[0] ||
        !layout.qHyperCube.qDataPages[0].qMatrix
    ) {
      return;
    }

    const chart = this.getChart(visualisation, layout, model);
    this.activeCharts[visualisation.id] = c3.generate(chart);
    this.applyChartDesignChanges(chart, visualisation);
  }

  /**
   * Apply any post generation chart design requirements
   * @param chart the c3 chart created
   * @param visualisation the visualisation used to create the chart
   */
  private applyChartDesignChanges(chart: InterACT.Interfaces.IC3Chart, visualisation: InterACT.Interfaces.IVisualisation) {
    if (visualisation.type === this.chartTypes.bar) {
      const allItemsAreZero = chart.data.json.every((x) => x.value === 0);
      if (allItemsAreZero) {
        d3.selectAll(`#chart_${visualisation.id} .c3-texts > text`).attr('dy', '-20px');
      }
    }
  }

  /**
   * Provides chart setup for c3 generation
   * @param visualisation the visualisation to create
   * @param layout the data response for the visualisation from qlik
   * @param model the model with actions from qlik
   * @returns the chart setup
   */
  private getChart(
    visualisation: InterACT.Interfaces.IVisualisation,
    layout,
    model
  ) {
    switch(visualisation.type) {
      case this.chartTypes.bar:
        return this.getBarChart(visualisation, layout, model);
      case this.chartTypes.line:
        return this.getLineChart(visualisation, layout, model);
      case this.chartTypes.pie:
        return this.getPieChart(visualisation, layout, model);
      default:
        break;
    }
  }

  /**
   * Gets the bar chart data setup for c3 generation
   * @param visualisation the visualisation to create a bar chart for
   * @param layout the data response for the visualisation from qlik
   * @param model the model with actions from qlik
   * @returns the constructed bar chart object
   */
  private getBarChart(
    visualisation: InterACT.Interfaces.IVisualisation,
    layout,
    model
  ) {
    const chart = this.getBaseChartObject(visualisation);
    chart.data = this.getData(visualisation, layout, model);
    chart.bar = {
      width: 60
    };

    if (visualisation.colourExpression) {
      chart.data.color = (color: string, field: string | Object) => {
        if (_.isNumber(field['index'])) {
          return layout.qHyperCube.qDataPages[0].qMatrix[field['index']][1].qAttrExps.qValues[0].qText;
        }
        return color;
      };
    }
    return chart;
  }

  /**
   * Gets the line chart data setup for c3 generation
   * @param visualisation the visualisation to create a line chart for
   * @param layout the data response for the visualisation from qlik
   * @param model the model with actions from qlik
   * @returns the constructed line chart object
   */
  private getLineChart(
    visualisation: InterACT.Interfaces.IVisualisation,
    layout,
    model
  ) {
    const chart = this.getBaseChartObject(visualisation);
    chart.data = this.getData(visualisation, layout, model);
    return chart;
  }

  /**
   * Gets the pie chart data setup for c3 generation
   * @param visualisation the visualisation to create a pie chart for
   * @param layout the data response for the visualisation from qlik
   * @param model the model with actions from qlik
   * @returns the constructed pie chart object
   */
  private getPieChart(
    visualisation: InterACT.Interfaces.IVisualisation,
    layout,
    model
  ) {
    const chart: InterACT.Interfaces.IC3Chart = {
      bindto: `#chart_${visualisation.id}`,
      tooltip: {
        show: false
      },
      padding: {
        left: 16,
        top: 24,
        bottom: 8,
        right: 16
      },
      legend: {
        show: true
      }
    };
    chart.data = this.getPieData(visualisation, layout, model);
    chart.pie = {
      label: {
        show: true
      }
    };

    return chart;
  }

  /**
   * Gets the data object for the visualisation from the layout
   * @param visualisation the visualisation to create
   * @param layout the data response for the visualisation from qlik
   * @param model the model with actions from qlik
   * @returns the data object
   */
  private getData(visualisation: InterACT.Interfaces.IVisualisation, layout, model) {
    const elemNumberLookup = {};
    const json = layout.qHyperCube.qDataPages[0].qMatrix.map((item) => {
      elemNumberLookup[item[0].qText] = item[0].qElemNumber;
      return {
        name: item[0].qText,
        elemNumber: item[0].qElemNumber,
        value: this.roundNumber(this.setNaNToZero(item[1].qNum), 1)
      };
    });
    return {
      keys: {
        x: 'name',
        value: ['value'],
        elemtNumber: 'elemNumber'
      },
      json: json,
      type: visualisation.type,
      labels: {
        format: (value) => {
          return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        }
      },
      onclick: (data, ev, something, el) => {
        const thisChart = this.activeCharts[visualisation.id];
        const elemNumber = elemNumberLookup[thisChart.categories()[data.x]];
        model.selectHyperCubeValues('/qHyperCubeDef', 0, [elemNumber], true);
      }
    };
  }

  /**
   * Gets the pie chart data object for the visualisation from the layout
   * @param visualisation the visualisation to create
   * @param layout the data response for the visualisation from qlik
   * @param model the model with actions from qlik
   * @returns the pie chart data object
   */
  private getPieData(visualisation: InterACT.Interfaces.IVisualisation, layout, model) {
    const elemNumberLookup = {};
    const columns = layout.qHyperCube.qDataPages[0].qMatrix.map((item) => {
      elemNumberLookup[item[0].qText] = item[0].qElemNumber;
      return [
        item[0].qText,
        this.roundNumber(this.setNaNToZero(item[1].qNum), 1)
      ];
    });
    return {
      columns: columns,
      type: visualisation.type,
      onclick: (data, ev, something, el) => {
        const thisChart = this.activeCharts[visualisation.id];
        const elemNumber = elemNumberLookup[thisChart.categories()[data.x]];
        model.selectHyperCubeValues('/qHyperCubeDef', 0, [elemNumber], true);
      }
    };
  }

  /**
   * Gets the chart base object
   * @param visualisation the visualisation to create a chart for
   * @returns the constructed base chart object
   */
  private getBaseChartObject(visualisation: InterACT.Interfaces.IVisualisation) {
    return <InterACT.Interfaces.IC3Chart> {
      bindto: `#chart_${visualisation.id}`,
      tooltip: {
        show: false
      },
      padding: {
        left: 16,
        top: 8,
        bottom: 32,
        right: 16
      },
      legend: {
        show: false
      },
      axis: {
        x: {
          type: 'category'
        },
        y: {
          show: false
        }
      }
    };
  }

  /**
   * Sets any value of not a number to zero
   * @param value the item that may not be a number
   * @returns the value set to 0 if Nan was entered
   */
  private setNaNToZero(value: number) {
    return isNaN(value) ? 0 : value;
  }

  /**
   * Rounds the value entered to the number of decimal places entered
   * @param value the value to round
   * @param nrDecimals the number of decimal places
   * @returns the rounded number
   */
  private roundNumber(value: number, nrDecimals: number) {
    const multiplier = 10 ** nrDecimals;
    return Math.round(value * multiplier) / multiplier;
  }
}